import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { RefreshCw, Star, Clock, AlertCircle, CheckCircle, MapPin, Calendar, Image, Eye, EyeOff, ArrowUp, ArrowDown } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { differenceInDays } from "date-fns";

interface Review {
  id: string;
  reviewer_name: string;
  reviewer_country?: string | null;
  reviewer_country_code?: string | null;
  reviewer_location?: string | null;
  visited_date: string | null;
  reviewed_date: string | null;
  title: string | null;
  rating: number;
  review_text: string | null;
  photos: string[];
  created_at: string;
  updated_at: string;
  is_hidden: boolean;
  sort_order: number;
}

type ReviewSource = "safaribookings" | "tripadvisor";

const AdminReviews = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSyncing, setIsSyncing] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [activeSource, setActiveSource] = useState<ReviewSource>("safaribookings");

  // Fetch last scrape log for SafariBookings
  const { data: lastLogSB, isLoading: isLoadingLogSB } = useQuery({
    queryKey: ["scrape-logs", "safaribookings", "last"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("scrape_logs")
        .select("*")
        .eq("source", "safaribookings")
        .order("executed_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });

  // Fetch last scrape log for TripAdvisor
  const { data: lastLogTA, isLoading: isLoadingLogTA } = useQuery({
    queryKey: ["scrape-logs", "tripadvisor", "last"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("scrape_logs")
        .select("*")
        .eq("source", "tripadvisor")
        .order("executed_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });

  // Fetch SafariBookings reviews
  const { data: reviewsSB, isLoading: isLoadingReviewsSB } = useQuery({
    queryKey: ["safaribookings-reviews"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("safaribookings_reviews")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Review[];
    },
  });

  // Fetch TripAdvisor reviews
  const { data: reviewsTA, isLoading: isLoadingReviewsTA } = useQuery({
    queryKey: ["tripadvisor-reviews"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tripadvisor_reviews")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data || []).map(r => ({ ...r, reviewer_country: r.reviewer_location })) as Review[];
    },
  });

  const lastLog = activeSource === "safaribookings" ? lastLogSB : lastLogTA;
  const isLoadingLog = activeSource === "safaribookings" ? isLoadingLogSB : isLoadingLogTA;
  const reviews = activeSource === "safaribookings" ? reviewsSB : reviewsTA;
  const isLoadingReviews = activeSource === "safaribookings" ? isLoadingReviewsSB : isLoadingReviewsTA;
  const tableName = activeSource === "safaribookings" ? "safaribookings_reviews" : "tripadvisor_reviews";

  // Toggle visibility mutation
  const toggleVisibilityMutation = useMutation({
    mutationFn: async ({ id, isHidden }: { id: string; isHidden: boolean }) => {
      const { error } = await supabase
        .from(tableName)
        .update({ is_hidden: isHidden })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`${activeSource}-reviews`] });
      toast({
        title: "Visibilidad actualizada",
        description: "El estado de la review ha sido actualizado",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update sort order mutation
  const updateSortOrderMutation = useMutation({
    mutationFn: async ({ id, sortOrder }: { id: string; sortOrder: number }) => {
      const { error } = await supabase
        .from(tableName)
        .update({ sort_order: sortOrder })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`${activeSource}-reviews`] });
      queryClient.invalidateQueries({ queryKey: [`${activeSource}-reviews-preview`] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const moveReview = (index: number, direction: "up" | "down") => {
    if (!reviews) return;
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= reviews.length) return;

    const currentReview = reviews[index];
    const targetReview = reviews[newIndex];

    // Swap sort orders
    updateSortOrderMutation.mutate({ id: currentReview.id, sortOrder: targetReview.sort_order });
    updateSortOrderMutation.mutate({ id: targetReview.id, sortOrder: currentReview.sort_order });
  };

  // Sync mutation for SafariBookings
  const syncSBMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await supabase.functions.invoke("scrape-safaribookings", {
        headers: session ? {
          Authorization: `Bearer ${session.access_token}`,
        } : undefined,
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["scrape-logs"] });
      queryClient.invalidateQueries({ queryKey: ["safaribookings-reviews"] });
      
      toast({
        title: "Sincronización completada",
        description: data.message || `Se obtuvieron ${data.reviewsScraped} reviews`,
      });
    },
    onError: (error) => {
      toast({
        title: "Error de sincronización",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Sync mutation for TripAdvisor
  const syncTAMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await supabase.functions.invoke("scrape-tripadvisor", {
        headers: session ? {
          Authorization: `Bearer ${session.access_token}`,
        } : undefined,
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["scrape-logs"] });
      queryClient.invalidateQueries({ queryKey: ["tripadvisor-reviews"] });
      
      toast({
        title: "Sincronización completada",
        description: data.message || `Se obtuvieron ${data.reviewsScraped} reviews`,
      });
    },
    onError: (error) => {
      toast({
        title: "Error de sincronización",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Check if sync is allowed (at least 7 days since last sync)
  const canSync = () => {
    if (!lastLog?.executed_at) return true;
    const lastSyncDate = new Date(lastLog.executed_at);
    const daysSinceLastSync = differenceInDays(new Date(), lastSyncDate);
    return daysSinceLastSync >= 7;
  };

  const getDaysUntilNextSync = () => {
    if (!lastLog?.executed_at) return 0;
    const lastSyncDate = new Date(lastLog.executed_at);
    const daysSinceLastSync = differenceInDays(new Date(), lastSyncDate);
    return Math.max(0, 7 - daysSinceLastSync);
  };

  const handleSync = async () => {
    if (!canSync()) {
      toast({
        title: "Sincronización no disponible",
        description: `Debes esperar ${getDaysUntilNextSync()} días más para sincronizar`,
        variant: "destructive",
      });
      return;
    }
    
    setIsSyncing(true);
    try {
      if (activeSource === "safaribookings") {
        await syncSBMutation.mutateAsync();
      } else {
        await syncTAMutation.mutateAsync();
      }
    } finally {
      setIsSyncing(false);
    }
  };

  const renderStars = (rating: number, size: "sm" | "lg" = "sm") => {
    const iconSize = size === "lg" ? "h-5 w-5" : "h-4 w-4";
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${iconSize} ${
              star <= rating ? "fill-yellow-400 text-yellow-400" : "text-muted"
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Gestión de Reviews</h1>
          <p className="text-muted-foreground">
            Sincroniza y gestiona las reviews desde diferentes fuentes
          </p>
        </div>
      </div>

      <Tabs value={activeSource} onValueChange={(v) => setActiveSource(v as ReviewSource)}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="safaribookings">SafariBookings</TabsTrigger>
          <TabsTrigger value="tripadvisor">TripAdvisor</TabsTrigger>
        </TabsList>

        <TabsContent value={activeSource} className="space-y-6 mt-6">
          {/* Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Estado de Sincronización - {activeSource === "safaribookings" ? "SafariBookings" : "TripAdvisor"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-4">
                {/* Last Sync */}
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Última sincronización</p>
                  <p className="font-medium">
                    {isLoadingLog ? (
                      "Cargando..."
                    ) : lastLog?.executed_at ? (
                      formatDistanceToNow(new Date(lastLog.executed_at), {
                        addSuffix: true,
                        locale: es,
                      })
                    ) : (
                      "Nunca"
                    )}
                  </p>
                </div>

                {/* Global Rating */}
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Rating Global</p>
                  <div className="flex items-center gap-2">
                    <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    <span className="font-medium text-lg">
                      {lastLog?.global_rating?.toFixed(1) || "-"}
                    </span>
                <span className="text-muted-foreground">/ 5</span>
              </div>
            </div>

            {/* Total Reviews */}
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Total Reviews</p>
              <p className="font-medium text-lg">
                {lastLog?.total_reviews || reviews?.length || 0}
              </p>
            </div>

            {/* Status */}
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Último Estado</p>
              {lastLog?.status === "success" ? (
                <Badge variant="default" className="bg-green-600">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Exitoso
                </Badge>
              ) : lastLog?.status === "error" ? (
                <Badge variant="destructive">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Error
                </Badge>
              ) : (
                <Badge variant="secondary">Sin datos</Badge>
              )}
            </div>
          </div>

          {lastLog?.error_message && (
            <div className="mt-4 p-3 bg-destructive/10 rounded-md">
              <p className="text-sm text-destructive">{lastLog.error_message}</p>
            </div>
          )}

          <div className="mt-6 flex items-center gap-4">
            <Button onClick={handleSync} disabled={isSyncing || !canSync()}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? "animate-spin" : ""}`} />
              {isSyncing ? "Sincronizando..." : "Sincronizar Reviews"}
            </Button>
            {!canSync() && (
              <span className="text-sm text-muted-foreground">
                Disponible en {getDaysUntilNextSync()} días
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Reviews Table */}
      <Card>
        <CardHeader>
          <CardTitle>Reviews Almacenadas</CardTitle>
          <CardDescription>
            {reviews?.length || 0} reviews en la base de datos. Haz clic en una review para ver el detalle.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingReviews ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : reviews && reviews.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">Orden</TableHead>
                  <TableHead>Reviewer</TableHead>
                  <TableHead>País</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Título</TableHead>
                  <TableHead>Fecha Visita</TableHead>
                  <TableHead>Fotos</TableHead>
                  <TableHead>Visible</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviews.map((review, index) => (
                  <TableRow 
                    key={review.id} 
                    className={`cursor-pointer hover:bg-muted/50 ${review.is_hidden ? "opacity-50" : ""}`}
                    onClick={() => setSelectedReview(review)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => moveReview(index, "up")}
                          disabled={index === 0 || updateSortOrderMutation.isPending}
                        >
                          <ArrowUp className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => moveReview(index, "down")}
                          disabled={index === reviews.length - 1 || updateSortOrderMutation.isPending}
                        >
                          <ArrowDown className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      {review.reviewer_name}
                    </TableCell>
                    <TableCell>
                      {review.reviewer_country_code ? (
                        <Badge variant="outline">{review.reviewer_country_code}</Badge>
                      ) : "-"}
                    </TableCell>
                    <TableCell>{renderStars(review.rating)}</TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {review.title || "-"}
                    </TableCell>
                    <TableCell>{review.visited_date || "-"}</TableCell>
                    <TableCell>
                      {review.photos && review.photos.length > 0 ? (
                        <Badge variant="secondary">
                          <Image className="h-3 w-3 mr-1" />
                          {review.photos.length}
                        </Badge>
                      ) : "-"}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Switch
                        checked={!review.is_hidden}
                        onCheckedChange={(checked) =>
                          toggleVisibilityMutation.mutate({ id: review.id, isHidden: !checked })
                        }
                        disabled={toggleVisibilityMutation.isPending}
                      />
                    </TableCell>
                    <TableCell>
                      {review.is_hidden ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No hay reviews almacenadas. Haz clic en "Sincronizar Reviews" para obtenerlas.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Detail Dialog */}
      <Dialog open={!!selectedReview} onOpenChange={() => setSelectedReview(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Detalle de Review
            </DialogTitle>
          </DialogHeader>
          
          {selectedReview && (
            <ScrollArea className="max-h-[70vh] pr-4">
              <div className="space-y-6">
                {/* Header with name and rating */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-semibold">{selectedReview.reviewer_name}</h3>
                    {selectedReview.reviewer_country && (
                      <div className="flex items-center gap-1 text-muted-foreground mt-1">
                        <MapPin className="h-4 w-4" />
                        <span>{selectedReview.reviewer_country}</span>
                        {selectedReview.reviewer_country_code && (
                          <Badge variant="outline" className="ml-2">
                            {selectedReview.reviewer_country_code}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    {renderStars(selectedReview.rating, "lg")}
                    <p className="text-sm text-muted-foreground mt-1">
                      {selectedReview.rating}/5
                    </p>
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedReview.visited_date && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Visitó:</span>
                      <span className="font-medium">{selectedReview.visited_date}</span>
                    </div>
                  )}
                  {selectedReview.reviewed_date && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Reseñó:</span>
                      <span className="font-medium">{selectedReview.reviewed_date}</span>
                    </div>
                  )}
                </div>

                {/* Title */}
                {selectedReview.title && (
                  <div>
                    <h4 className="font-semibold text-lg">{selectedReview.title}</h4>
                  </div>
                )}

                {/* Review Text */}
                {selectedReview.review_text ? (
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">
                      {selectedReview.review_text}
                    </p>
                  </div>
                ) : (
                  <div className="bg-muted/50 rounded-lg p-4 text-center text-muted-foreground">
                    <p className="text-sm">No hay texto de review disponible</p>
                  </div>
                )}

                {/* Photos */}
                {selectedReview.photos && selectedReview.photos.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Image className="h-4 w-4" />
                      Fotos ({selectedReview.photos.length})
                    </h4>
                    <div className="grid grid-cols-3 gap-2">
                      {selectedReview.photos.map((photo, index) => (
                        <a 
                          key={index} 
                          href={photo} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="aspect-square rounded-lg overflow-hidden bg-muted hover:opacity-80 transition-opacity"
                        >
                          <img 
                            src={photo} 
                            alt={`Foto ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Metadata */}
                <div className="text-xs text-muted-foreground border-t pt-4">
                  <p>ID: {selectedReview.id}</p>
                  <p>Última actualización: {new Date(selectedReview.updated_at).toLocaleString('es')}</p>
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminReviews;
